import { useState } from 'react';
import { BankDetailsPanel } from './BankDetailsPanel';
import { HandoverDaysPanel } from './HandoverDaysPanel';
import { CAPACITY_LINES, type CapacityBlock, type CapacityLine } from '@bbbk/schema';
import { Icon, useToast, writeWithToast } from '../common';
import { AccountTabSkeleton } from './AccountTabSkeleton';
import { api } from '../../lib/api';
import { useResource } from '../../lib/useResource';
import { errMsg } from './utils';
import {
  EMPTY_DRAFT,
  MAX_NOTE_LENGTH,
  draftToBody,
  isDirty,
  standingEdits,
  type LimitKey,
  limitLabel,
  rangeLabel,
  sizeLabel,
  sortBlocks,
  standingLimits,
  validateDraft,
  type BlockDraft,
} from './sizeLimits';

// Account › Customer portal — the Size limits block (portal-foundations AC15).
//
// THE STORY'S ONE SCREEN, and the whole of it. R-SET-3: the two standing
// per-size limits, and a dated override list added and removed inline. The rest
// of R-SET-1's keys exist and default correctly (AC11) but nothing reads them
// yet — `availability-and-request` opens the page that does, and putting
// eighteen fields here now would be a form for values with no consumer.
//
// It follows the desk's existing settings idiom rather than inventing one: the
// `.fin-panel` card the Finances and Account pages already use, the `.field`
// controls from the modals, and the `.btn` set. The prototype's Account ▸
// Customer portal tab (screen s4) is the frame; its Capacity card drew the
// SINGLE-LINE model that per-size capacity replaced on 2026-09-09, so the block
// below is R-SET-3's text in the prototype's card, which is the closest thing
// to a visual contract that exists for it.
//
// NOTHING READS THESE LIMITS YET. Storing one nobody enforces is the same
// bargain as `requested` sitting unreachable in the status list.

interface SettingsEnvelope {
  minimumWage: number;
  portal: Record<string, string>;
}

export function PortalSettingsView() {
  const toast = useToast();

  const settingsRes = useResource(
    'portal:settings',
    () => api<SettingsEnvelope>('/settings').then((r) => r.data),
    { invalidatedBy: ['settings'] },
  );
  const blocksRes = useResource(
    'portal:capacity-blocks',
    () => api<CapacityBlock[]>('/capacity-blocks').then((r) => r.data),
    { invalidatedBy: ['settings'] },
  );

  const portal = settingsRes.state.data?.portal;
  const blocks = blocksRes.state.data ?? [];
  // A FAILED fetch is not an empty list, and the two must not render alike.
  // Found by running it: with `capacity_blocks` absent the route 500s, and the
  // screen said "No overrides. The standing limits above apply every day." —
  // which is a statement about the kennel, made on the strength of a request
  // that never answered. Every test passed; only the browser showed it.
  const blocksFailed = blocksRes.state.phase === 'error';

  // The two standing numbers are edited in place. Seeded from the server on
  // first read; `null` until then, so the inputs are not rendered with an
  // invented zero that Patricia could save by accident.
  const [edits, setEdits] = useState<Partial<Record<LimitKey, string>>>({});
  const [draft, setDraft] = useState<BlockDraft>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);

  if (settingsRes.state.phase === 'loading' || blocksRes.state.phase === 'loading') {
    // The shared Account tab skeleton, not a bespoke one — the three tabs
    // deliberately cannot drift into different loading looks.
    return <AccountTabSkeleton label="Loading the portal settings…" rows={3} />;
  }
  if (!portal) {
    return (
      <div className="account-error">
        <Icon name="alert" /> Could not load the portal settings.
      </div>
    );
  }

  const standing = standingLimits(portal);
  const valueFor = (line: CapacityLine, which: 'soft' | 'hard') =>
    edits[`${line}_${which}`] ?? String(standing[line][which] ?? '');
  // Both of these are sizeLimits.ts's rules now, not the component's own copy.
  // The copy is what let a CLEARED field save as 0 — i.e. closed — while the
  // add row beneath it refused the same blank (PR #204 review).
  const dirty = isDirty(edits, standing);

  const saveStanding = () => {
    const parsed = standingEdits(edits, standing);
    if ('error' in parsed) {
      toast.error(parsed.error);
      return;
    }
    const body = parsed.writes;
    if (Object.keys(body).length === 0) return;
    setSaving(true);
    void writeWithToast(toast, {
      write: () => api('/settings', { method: 'PATCH', body }),
      success: 'Size limits saved',
      error: (e) => errMsg(e, 'Could not save the size limits.'),
      onSaved: () => {
        setEdits({});
        settingsRes.reload();
      },
      onError: () => setSaving(false),
    }).finally(() => setSaving(false));
  };

  const addBlock = () => {
    const invalid = validateDraft(draft);
    if (invalid) {
      toast.error(invalid.error);
      return;
    }
    setSaving(true);
    void writeWithToast(toast, {
      write: () => api('/capacity-blocks', { method: 'POST', body: draftToBody(draft) }),
      success: 'Override added',
      // THE OVERLAP MESSAGE COMES FROM THE SERVER (AC15): it names the range it
      // collides with, and only the server knows what is stored. errMsg prefers
      // the server's own words and falls back only when there are none.
      error: (e) => errMsg(e, 'Could not add that override.'),
      onSaved: () => {
        setDraft(EMPTY_DRAFT);
        blocksRes.reload();
      },
    }).finally(() => setSaving(false));
  };

  const removeBlock = (block: CapacityBlock) => {
    void writeWithToast(toast, {
      write: () => api(`/capacity-blocks/${block.id}`, { method: 'DELETE' }),
      success: 'Override removed',
      error: (e) => errMsg(e, 'Could not remove that override.'),
      onSaved: () => blocksRes.reload(),
    });
  };

  return (
    <div className="pf-tab">
      {/* THE BANK DETAILS FIRST (deposit-proof AC16): the one setting the
          customer's money depends on. Keyed on the stored values so a save
          re-seeds the fields from the server, never from the draft. */}
      <BankDetailsPanel
        key={`${portal.bank_account_name ?? ''}|${portal.bank_iban ?? ''}`}
        accountName={portal.bank_account_name ?? ''}
        iban={portal.bank_iban ?? ''}
        onSaved={() => settingsRes.reload()}
      />
      <section className="fin-panel">
        <div className="fin-panel-head">
          <h3>Size limits</h3>
          <span className="panel-meta">How many of each size the portal will take</span>
        </div>

        <p className="pf-note">
          Counted in <strong>dogs</strong>, not bookings — a household with one big and one little
          takes one place of each. Dogs on the books as mixed, house or unsized count towards the
          whole kennel only, which is what that line is for.
        </p>
        <p className="pf-note">
          Below <strong>Limited from</strong> a day shows green. Between the two it shows amber —
          the customer sees <em>limited space available</em> and can still ask. At{' '}
          <strong>Full at</strong> it shows red and they cannot send a request.
        </p>

        <div className="pf-standing">
          {/* THREE LINES, TWO NUMBERS EACH. §8 has always promised
              `free | limited | full`; between 21:50 on 2026-09-09 and now,
              R-SET-1 named one number per line and the amber was
              unexpressible. Freddi restored the pair on 2026-09-10. */}
          {CAPACITY_LINES.map((line) => (
            <div className="pf-line" key={line}>
              <span className="pf-line-name">{sizeLabel(line)}</span>
              {(['soft', 'hard'] as const).map((which) => (
                <label className={'field pf-' + which} key={which}>
                  <span className="field-label">{which === 'soft' ? 'Limited from' : 'Full at'}</span>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    inputMode="numeric"
                    value={valueFor(line, which)}
                    aria-label={`${which === 'soft' ? 'Warn from' : 'Full at'} — ${sizeLabel(line).toLowerCase()}`}
                    onChange={(e) =>
                      setEdits((prev) => ({ ...prev, [`${line}_${which}`]: e.target.value }))
                    }
                  />
                </label>
              ))}
            </div>
          ))}
          <button type="button" className="btn primary" disabled={!dirty || saving} onClick={saveStanding}>
            Save
          </button>
        </div>
      </section>

      <section className="fin-panel">
        <div className="fin-panel-head">
          <h3>Dated overrides</h3>
          <span className="panel-meta">A different limit for a stretch of days</span>
        </div>

        {blocksFailed ? (
          <div className="account-error">
            <Icon name="alert" />
            Could not load the overrides — the standing limits above may not be the
            whole picture.
            <button type="button" className="btn ghost sm" onClick={() => blocksRes.reload()}>
              Try again
            </button>
          </div>
        ) : blocks.length === 0 ? (
          <p className="pf-note pf-empty">
            No overrides. The standing limits above apply every day.
          </p>
        ) : (
          <ul className="pf-blocks">
            {/* A header row, because the columns had no names — a range read
                "CLOSED  Christmas" with nothing saying that the second word was
                the reason. Same eyebrow styling as the field labels beneath, so
                the list and the row that adds to it read as one thing. */}
            <li className="pf-block pf-block-head" aria-hidden="true">
              <span>Line</span>
              <span>When</span>
              <span>Limited</span>
              <span>Full</span>
              <span>Reason</span>
              <span />
            </li>
            {sortBlocks(blocks).map((block) => (
              <li className="pf-block" key={block.id}>
                <span className="pf-block-size">{sizeLabel(block.line)}</span>
                <span className="pf-block-range">{rangeLabel(block)}</span>
                {/* An override may carry no amber band at all — a closed week
                    wants none — so a null soft reads as a dash, not as 0. */}
                <span className={'pf-block-limit' + (block.softLimit === null ? ' none' : '')}>
                  {block.softLimit === null ? '—' : block.softLimit}
                </span>
                {/* `0` reads as CLOSED, not as a number — AC15. A limit column
                    showing "0" reads as a broken value or a missing one. */}
                <span className={'pf-block-limit' + (block.hardLimit === 0 ? ' closed' : '')}>
                  {limitLabel(block.hardLimit)}
                </span>
                {/* ALWAYS RENDERED, empty when there is no note. The grid has
                    five columns and a conditionally-mounted cell is a column
                    that disappears — which slid the remove button out of its
                    own track and left two rows in the list disagreeing about
                    where anything sits. Visible the moment two rows existed,
                    one with a note and one without; invisible to every test,
                    because renderToStaticMarkup has no grid. */}
                <span className={'pf-block-note' + (block.note ? '' : ' none')}>
                  {block.note ?? '—'}
                </span>
                <button
                  type="button"
                  className="btn ghost sm"
                  aria-label={`Remove the ${sizeLabel(block.line).toLowerCase()} override for ${rangeLabel(block)}`}
                  onClick={() => removeBlock(block)}
                >
                  <Icon name="close" />
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="pf-add">
          <label className="field">
            <span className="field-label">Line</span>
            <select
              value={draft.line}
              aria-label="Line for the new override"
              onChange={(e) => setDraft((d) => ({ ...d, line: e.target.value as CapacityLine }))}
            >
              {CAPACITY_LINES.map((line) => (
                <option key={line} value={line}>
                  {sizeLabel(line)}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field-label">From</span>
            <input
              type="date"
              value={draft.startsOn}
              aria-label="First day of the new override"
              onChange={(e) => setDraft((d) => ({ ...d, startsOn: e.target.value }))}
            />
          </label>
          <label className="field">
            <span className="field-label">To</span>
            <input
              type="date"
              value={draft.endsOn}
              aria-label="Last day of the new override"
              onChange={(e) => setDraft((d) => ({ ...d, endsOn: e.target.value }))}
            />
          </label>
          <label className="field">
            <span className="field-label">Limited from</span>
            <input
              type="number"
              min={0}
              step={1}
              inputMode="numeric"
              placeholder="optional"
              value={draft.soft}
              aria-label="Warn from — optional"
              onChange={(e) => setDraft((d) => ({ ...d, soft: e.target.value }))}
            />
          </label>
          <label className="field">
            <span className="field-label">Full at</span>
            <input
              type="number"
              min={0}
              step={1}
              inputMode="numeric"
              placeholder="0 = closed"
              value={draft.hard}
              aria-label="Full at — 0 means closed"
              onChange={(e) => setDraft((d) => ({ ...d, hard: e.target.value }))}
            />
          </label>
          {/* WHY. The column has existed since migration 0027 and the API has
              accepted it since the first commit; this input is what was
              missing, so a reason could only be set by curl. Optional — the
              label asks, it does not demand. */}
          <label className="field pf-reason">
            <span className="field-label">Reason</span>
            <input
              type="text"
              maxLength={MAX_NOTE_LENGTH}
              placeholder="Christmas, kennel refit…"
              value={draft.note}
              aria-label="Why this range is limited — optional"
              onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value }))}
            />
          </label>
          <button
            type="button"
            className="btn primary"
            disabled={saving || blocksFailed}
            onClick={addBlock}
          >
            Add
          </button>
        </div>

        <p className="pf-note">
          Both days are included. <strong>0</strong> in <em>Full at</em> closes that line for the
          whole stretch; leave <em>Limited from</em> blank for no warning. An override replaces the
          standing pair for those days — higher or lower. Overlapping ranges on one line are
          refused; the three lines are independent.
        </p>
      </section>

      {/* RESTRICTED DROP-OFF AND COLLECTION DAYS (handover-days). The weekly
          rule is a portal setting, so it is read from — and reloads — the same
          settings this tab already holds; the dated rows are its own read. */}
      <HandoverDaysPanel
        closedWeekly={weeklyList(portal.handover_closed_weekly)}
        pmWindow={portal.handover_pm ?? '17:00-18:00'}
        onWeeklySaved={() => settingsRes.reload()}
      />
    </div>
  );
}

/** `handover_closed_weekly` as stored — a JSON list — read defensively: a bad value is no rule, not a crash. */
function weeklyList(raw: string | undefined): string[] {
  try {
    const v: unknown = JSON.parse(raw ?? '[]');
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}
